import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, MessageCircle, Search } from 'lucide-react';
import { Badge, Card, EmptyState, ErrorState, LoadingState, SectionHeader } from '@pixlide/ui';
import { useArchivedConversationsAcrossTenants, useConversations } from '@/api/queries/messages';
import { usePlatformAdmins } from '@/api/queries/platform-admin';
import { queryKeys } from '@/api/query-client';
import { createConversationHubConnection } from '@/lib/signalr';
import { AssignmentControl } from '@/features/messages/AssignmentControl';
import type { components } from '@/api/generated/schema';

type MessageResponse = components['schemas']['PixlideApplicationFeaturesSupportMessageResponse'];

const PAGE_SIZE = 20;
const ARCHIVE_PAGE_SIZE = 20;

/** Couleurs d'avatar cycliques — même pattern que `TenantsPage.tsx`, sans signification métier. */
const AVATAR_TONES = [
  'bg-brand-100 text-brand-700',
  'bg-success-100 text-success-500',
  'bg-warning-100 text-warning-500',
  'bg-ink-100 text-ink-700',
];

function avatarTone(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash + seed.charCodeAt(i)) % AVATAR_TONES.length;
  return AVATAR_TONES[hash];
}

/** Initiales du nom de tenant, pour l'avatar de la liste (pas de logo tenant côté API). */
function initials(tenantName: string) {
  const words = tenantName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}

/**
 * Liste des conversations support ACTIVES, triées par activité récente — les conversations
 * fermées n'y apparaissent plus (voir l'onglet « Archivées »), pour que la liste ne montre
 * que ce qui a besoin d'attention.
 *
 * Ouvre une connexion SignalR au montage : sur `MessagePosted` (n'importe quel tenant), la
 * liste est invalidée pour remonter la conversation concernée en tête et rafraîchir son
 * badge de non-lus — la connexion rejoint automatiquement le groupe `platform-admins`
 * côté serveur à partir du JWT, rien à faire ici au-delà de se connecter.
 */
export function ConversationsPage() {
  const [tab, setTab] = useState<'active' | 'archive'>('active');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [archivePage, setArchivePage] = useState(1);
  const queryClient = useQueryClient();

  const conversations = useConversations({ page, pageSize: PAGE_SIZE });
  const archived = useArchivedConversationsAcrossTenants(archivePage, ARCHIVE_PAGE_SIZE);
  const platformAdmins = usePlatformAdmins();

  useEffect(() => {
    const connection = createConversationHubConnection();

    connection.on('MessagePosted', (_message: MessageResponse) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations() });
    });

    connection.on('ConversationStatusChanged', () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.archivedConversationsAcrossTenants() });
    });

    connection.on('ConversationAssigned', () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations() });
    });

    void connection.start();

    return () => {
      void connection.stop();
    };
  }, [queryClient]);

  const filteredItems = (conversations.data?.items ?? []).filter((item) =>
    search.trim() === '' ? true : item.tenantName.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const filteredArchiveItems = (archived.data?.items ?? []).filter((item) =>
    search.trim() === '' ? true : item.tenantName.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const admins = platformAdmins.data?.items ?? [];

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <SectionHeader
        title="Messages"
        description="Conversations support avec les tenants de la plateforme, triées par activité récente."
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit gap-1 rounded-pill bg-ink-50 p-1">
          <button
            type="button"
            onClick={() => setTab('active')}
            className={`rounded-pill px-4 py-1.5 text-sm font-medium transition ${
              tab === 'active' ? 'bg-white text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            Actives
          </button>
          <button
            type="button"
            onClick={() => setTab('archive')}
            className={`rounded-pill px-4 py-1.5 text-sm font-medium transition ${
              tab === 'archive' ? 'bg-white text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            Archivées
          </button>
        </div>

        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-300"
          />
          <input
            type="search"
            aria-label="Rechercher une conversation"
            placeholder="Nom du tenant…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-10 w-64 rounded-pill bg-white pr-4 pl-9 text-sm ring-1 ring-ink-100 placeholder:text-ink-300 focus:ring-brand-500"
          />
        </div>
      </div>

      {tab === 'active' ? (
        <>
          {conversations.isPending ? <LoadingState label="Chargement des conversations…" /> : null}

          {conversations.isError ? (
            <ErrorState
              message="Impossible de charger les conversations."
              onRetry={() => void conversations.refetch()}
            />
          ) : null}

          {conversations.data && filteredItems.length === 0 ? (
            <EmptyState
              icon={<MessageCircle className="size-10" strokeWidth={1.25} />}
              title="Aucune conversation active"
              description="Aucune conversation en cours ne correspond à cette recherche."
            />
          ) : null}

          {conversations.data && filteredItems.length > 0 ? (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Liste des conversations support actives</caption>

                  <thead className="bg-ink-50/60 text-left text-xs font-medium tracking-wide text-ink-500 uppercase">
                    <tr>
                      <th scope="col" className="px-5 py-3">Tenant</th>
                      <th scope="col" className="px-5 py-3">Dernier message</th>
                      <th scope="col" className="px-5 py-3">Activité</th>
                      <th scope="col" className="px-5 py-3">Assignation</th>
                      <th scope="col" className="px-5 py-3">
                        <span className="sr-only">Non lus</span>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-ink-100/70">
                    {filteredItems.map((item) => (
                      <tr key={item.tenantId}>
                        <th scope="row" className="px-5 py-4 text-left font-medium">
                          <Link
                            to="/messages/$tenantId"
                            params={{ tenantId: item.tenantId }}
                            className="flex items-center gap-3 hover:underline"
                          >
                            <span
                              aria-hidden
                              className={`grid size-8 shrink-0 place-items-center rounded-full text-xs font-semibold ${avatarTone(item.tenantId)}`}
                            >
                              {initials(item.tenantName)}
                            </span>
                            {item.tenantName}
                          </Link>
                        </th>

                        <td className="max-w-xs truncate px-5 py-4 text-ink-500">
                          {item.lastMessagePreview ?? '—'}
                        </td>

                        <td className="px-5 py-4 text-ink-500">
                          {item.lastMessageAt ? new Date(item.lastMessageAt).toLocaleString('fr-FR') : '—'}
                        </td>

                        <td className="px-5 py-4">
                          <AssignmentControl
                            tenantId={item.tenantId}
                            assignedAdminUserId={item.assignedAdminUserId ?? null}
                            assignedAdminName={item.assignedAdminName ?? null}
                            admins={admins}
                          />
                        </td>

                        <td className="px-5 py-4 text-right">
                          {item.unreadCount > 0 ? <Badge tone="brand">{item.unreadCount}</Badge> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-100/70 px-5 py-3 text-sm text-ink-500">
                <span>
                  Page {conversations.data.page} sur {Math.max(conversations.data.totalPages, 1)} ·{' '}
                  {conversations.data.totalCount} conversation{conversations.data.totalCount > 1 ? 's' : ''}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!conversations.data.hasPreviousPage}
                    onClick={() => setPage((current) => current - 1)}
                    aria-label="Page précédente"
                    className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft aria-hidden className="size-4" />
                  </button>

                  <button
                    type="button"
                    disabled={!conversations.data.hasNextPage}
                    onClick={() => setPage((current) => current + 1)}
                    aria-label="Page suivante"
                    className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronRight aria-hidden className="size-4" />
                  </button>
                </div>
              </div>
            </Card>
          ) : null}
        </>
      ) : null}

      {tab === 'archive' ? (
        <>
          {archived.isPending ? <LoadingState label="Chargement des archives…" /> : null}

          {archived.isError ? (
            <ErrorState message="Impossible de charger les archives." onRetry={() => void archived.refetch()} />
          ) : null}

          {archived.data && filteredArchiveItems.length === 0 ? (
            <EmptyState
              icon={<MessageCircle className="size-10" strokeWidth={1.25} />}
              title="Aucune conversation archivée"
              description="Les conversations fermées apparaîtront ici."
            />
          ) : null}

          {archived.data && filteredArchiveItems.length > 0 ? (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Liste des conversations support archivées</caption>

                  <thead className="bg-ink-50/60 text-left text-xs font-medium tracking-wide text-ink-500 uppercase">
                    <tr>
                      <th scope="col" className="px-5 py-3">Tenant</th>
                      <th scope="col" className="px-5 py-3">Dernier message</th>
                      <th scope="col" className="px-5 py-3">Fermée le</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-ink-100/70">
                    {filteredArchiveItems.map((item) => (
                      <tr key={item.conversationId}>
                        <th scope="row" className="px-5 py-4 text-left font-medium">
                          <Link
                            to="/messages/$tenantId"
                            params={{ tenantId: item.tenantId }}
                            className="flex items-center gap-3 hover:underline"
                          >
                            <span
                              aria-hidden
                              className={`grid size-8 shrink-0 place-items-center rounded-full text-xs font-semibold ${avatarTone(item.tenantId)}`}
                            >
                              {initials(item.tenantName)}
                            </span>
                            {item.tenantName}
                            <Badge tone="neutral">Fermée</Badge>
                          </Link>
                        </th>

                        <td className="max-w-xs truncate px-5 py-4 text-ink-500">
                          {item.lastMessagePreview ?? '—'}
                        </td>

                        <td className="px-5 py-4 text-ink-500">
                          {item.closedAt ? new Date(item.closedAt).toLocaleString('fr-FR') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-100/70 px-5 py-3 text-sm text-ink-500">
                <span>
                  Page {archived.data.page} sur {Math.max(archived.data.totalPages, 1)} ·{' '}
                  {archived.data.totalCount} conversation{archived.data.totalCount > 1 ? 's' : ''}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!archived.data.hasPreviousPage}
                    onClick={() => setArchivePage((current) => current - 1)}
                    aria-label="Page précédente"
                    className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft aria-hidden className="size-4" />
                  </button>

                  <button
                    type="button"
                    disabled={!archived.data.hasNextPage}
                    onClick={() => setArchivePage((current) => current + 1)}
                    aria-label="Page suivante"
                    className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronRight aria-hidden className="size-4" />
                  </button>
                </div>
              </div>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
